import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, FileText, Loader2, Package, XCircle } from "lucide-react";
import { API, api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function SuccessPage() {
  useSEO("Order Confirmed — The Mechanic's Tool Kit", "Your kit is confirmed. Download your files instantly.");
  const [params] = useSearchParams();
  const purchaseId = params.get("purchase_id");
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!purchaseId) {
      setError("missing");
      return;
    }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/purchases/${purchaseId}`);
        if (stop) return;
        setPurchase(data);
        if (data.status === "paid") clearInterval(timer);
      } catch (e) {
        if (!stop) setError("notfound");
        clearInterval(timer);
      }
    };
    poll();
    const timer = setInterval(poll, 2500);
    return () => { stop = true; clearInterval(timer); };
  }, [purchaseId]);

  return (
    <main className="pt-28 pb-24 min-h-screen blueprint-grid" data-testid="success-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        {error && (
          <div className="border border-red-500/30 bg-panel p-10 text-center">
            <XCircle className="w-10 h-10 text-red-400 mx-auto" />
            <h1 className="mt-4 font-display text-3xl font-black uppercase text-slate-50">Order not found</h1>
            <p className="mt-3 text-sm text-slate-400">This confirmation link is invalid or expired. If you completed a payment, contact support@mechanicstoolkit.com and we'll sort it out.</p>
            <Link to="/" className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.2em] text-blaze">← Back to the shop</Link>
          </div>
        )}

        {!error && (!purchase || purchase.status !== "paid") && (
          <div className="border border-slate-400/15 bg-panel p-10 text-center" data-testid="order-status">
            <Loader2 className="w-10 h-10 text-blaze mx-auto animate-spin" />
            <h1 className="mt-4 font-display text-3xl font-black uppercase text-slate-50">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto">
              Square is confirming the charge. This usually takes a few seconds — keep this page open
              and your downloads will unlock automatically.
            </p>
          </div>
        )}

        {!error && purchase?.status === "paid" && (
          <div data-testid="order-status">
            <div className="clip-notch border border-blaze/40 bg-panel p-8 sm:p-10">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-10 h-10 text-green-400 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blaze">Payment confirmed // Kit secured</p>
                  <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50">
                    {purchase.kit_name}
                  </h1>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-400">
                ${(purchase.amount / 100).toFixed(2)} {purchase.currency} — delivered instantly.
                Save these files somewhere safe; each link below re-generates a fresh copy for you.
              </p>
              {purchase.receipt_email && (
                <p data-testid="receipt-email-note" className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-green-400/90">
                  Receipt + download links emailed to {purchase.receipt_email}
                </p>
              )}
              <a
                data-testid="download-all-button"
                href={`${API}${purchase.download_all_url}`}
                className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-6 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)]"
              >
                <Package className="w-5 h-5" /> Download everything (.zip)
              </a>
            </div>

            <div className="mt-6 border border-slate-400/10 bg-panel">
              <p className="px-6 pt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Individual files // {purchase.documents.length} documents
              </p>
              <ul className="mt-2 divide-y divide-slate-400/5">
                {purchase.documents.map((d) => (
                  <li key={d.doc_id}>
                    <a
                      data-testid={`download-doc-${d.doc_id}`}
                      href={`${API}${d.url}`}
                      className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-raised/70 transition-colors group"
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-blaze shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-sm text-slate-200 truncate">{d.title}</span>
                          <span className="block font-mono text-[10px] text-slate-500 truncate">{d.filename}</span>
                        </span>
                      </span>
                      <Download className="w-4 h-4 text-slate-500 group-hover:text-blaze transition-colors shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Lost the files later? Email support@mechanicstoolkit.com — links are re-issued free, forever.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
